/**
 * MASCOT CHOREOGRAPHY
 *
 * Algorithmic positioning and posing for the guided tour mascot.
 * Replaces hardcoded pose maps with geometry-aware dock selection.
 */

import type { CSSProperties } from 'react';
import type {
  TourMascotConfig,
  TourStep,
  TourFlow,
} from '@/config/guided-tours/types';

export type MascotPointerDirection = 'up' | 'down' | 'left' | 'right' | 'left-right';
export type MascotDockPosition =
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right'
  | 'left-center'
  | 'right-center'
  | 'top-center'
  | 'bottom-center';
export type MascotSide = 'left' | 'right';
export type CardDockPosition = 'left' | 'right' | 'above' | 'below';
export type MobileCardAnchor = 'above' | 'below';

export interface MascotChoreography {
  side: MascotSide;
  position: 'left' | 'right' | 'top' | 'bottom';
  dockPosition: MascotDockPosition;
  pointingDirection: MascotPointerDirection;
  panelSide: MascotSide;
  flipStyle: CSSProperties | undefined;
  mascotImageStyle: CSSProperties | undefined;
  preferredPose: string;
  mobileCardAnchor: MobileCardAnchor;
}

export interface StepChoreography {
  pointerDirection: MascotPointerDirection;
  mascotDock: MascotDockPosition;
  desktopCardDock: CardDockPosition;
  mobileCardAnchor: MobileCardAnchor;
  preferredMascotPose: string;
}

const POINTER_SEQUENCE: MascotPointerDirection[] = [
  'left',
  'up',
  'right',
  'left-right',
  'down',
  'left',
];

const DOCK_SEQUENCE: MascotDockPosition[] = [
  'right-center',
  'bottom-left',
  'top-right',
  'left-center',
  'bottom-right',
  'top-left',
];

const CARD_DOCK_SEQUENCE: CardDockPosition[] = ['left', 'right', 'above', 'below'];
const MOBILE_ANCHOR_SEQUENCE: MobileCardAnchor[] = ['below', 'above'];

function hasText(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function normalizePose(pose: string | null | undefined): string {
  return hasText(pose) ? pose.trim() : 'intro_dude';
}

function getSequenceValue<T>(sequence: T[], index: number): T {
  return sequence[index % sequence.length] ?? sequence[0];
}

function resolvePositionFromDock(dock: MascotDockPosition): MascotChoreography['position'] {
  if (dock.startsWith('left')) return 'left';
  if (dock.startsWith('right')) return 'right';
  if (dock.startsWith('top')) return 'top';
  return 'bottom';
}

export function resolveSideFromDock(dock: MascotDockPosition): MascotSide {
  if (dock.endsWith('left') || dock === 'left-center') return 'left';
  if (dock.endsWith('right') || dock === 'right-center') return 'right';
  // Default to right for top/bottom center to keep the panel on the right.
  return 'right';
}

export function resolvePointingDirection(
  dock: MascotDockPosition,
  fallback: MascotPointerDirection
): MascotPointerDirection {
  switch (dock) {
    case 'left-center':
    case 'bottom-left':
    case 'top-left':
      return 'right';
    case 'right-center':
    case 'bottom-right':
    case 'top-right':
      return 'left';
    case 'top-center':
      return 'down';
    case 'bottom-center':
      return 'up';
    default:
      return fallback;
  }
}

export function getPoseNaturalDirection(
  pose: string | null | undefined
): 'left' | 'right' | null {
  if (!hasText(pose)) return null;

  const p = pose.trim().toLowerCase();
  if (
    p.includes('pointing_left') ||
    p.includes('left_pointing') ||
    p === 'pointing-left' ||
    p.endsWith('_left')
  ) {
    return 'left';
  }
  if (
    p.includes('pointing_right') ||
    p.includes('right_pointing') ||
    p === 'pointing-right' ||
    p.endsWith('_right')
  ) {
    return 'right';
  }

  return null;
}

export function resolveFlipStyle(
  dock: MascotDockPosition,
  pointingDirection: MascotPointerDirection,
  pose?: string | null
): CSSProperties | undefined {
  const natural = getPoseNaturalDirection(pose);

  if (natural === 'left' && pointingDirection === 'right') {
    return { transform: 'scaleX(-1)' };
  }

  if (natural === 'right' && pointingDirection === 'left') {
    return { transform: 'scaleX(-1)' };
  }

  return undefined;
}

function resolveMascotImageStyle(
  pose: string | null | undefined
): CSSProperties | undefined {
  if (pose === 'left-right') {
    return { transform: 'scaleX(1)' };
  }
  return undefined;
}

/**
 * Map human-friendly mascot position hints (e.g. 'left', 'right') to a
 * full dock position so YAML authors can write simple side names.
 */
function mapMascotPositionToDock(
  position: string,
  stepIndex: number
): MascotDockPosition {
  const normalized = position.trim().toLowerCase();
  const exact: Record<string, MascotDockPosition> = {
    left: 'left-center',
    right: 'right-center',
    top: 'top-center',
    bottom: 'bottom-center',
    center: 'bottom-center',
    'left-center': 'left-center',
    'right-center': 'right-center',
    'top-center': 'top-center',
    'bottom-center': 'bottom-center',
    'top-left': 'top-left',
    'top-right': 'top-right',
    'bottom-left': 'bottom-left',
    'bottom-right': 'bottom-right',
  };

  return exact[normalized] ?? getSequenceValue(DOCK_SEQUENCE, stepIndex);
}

/**
 * Compute the choreography for a single step based on its index and the
 * algorithm configured in the global mascot config.
 */
export function getStepChoreography(
  step: TourStep,
  stepIndex: number,
  mascotConfig?: TourMascotConfig | null
): StepChoreography {
  const algorithm = mascotConfig?.choreography?.algorithm ?? 'auto';
  const fallbackPose =
    mascotConfig?.default_poses?.pointing_left ?? 'pointing_left';
  const fallbackRightPose =
    mascotConfig?.default_poses?.pointing_right ?? 'right_pointing_dude';

  const hasMascotPosition = hasText(step.mascot_position);
  const usePositionHint =
    hasMascotPosition &&
    (algorithm === 'auto' || algorithm === 'hybrid' || algorithm === 'manual');

  let mascotDock: MascotDockPosition;
  if (usePositionHint) {
    mascotDock = mapMascotPositionToDock(step.mascot_position!, stepIndex);
  } else {
    mascotDock = getSequenceValue(DOCK_SEQUENCE, stepIndex);
  }

  const side = resolveSideFromDock(mascotDock);
  const pointerDirection = resolvePointingDirection(
    mascotDock,
    getSequenceValue(POINTER_SEQUENCE, stepIndex)
  );

  const preferredMascotPose = hasText(step.mascot_pose)
    ? normalizePose(step.mascot_pose)
    : side === 'left'
      ? fallbackPose
      : fallbackRightPose;

  return {
    pointerDirection,
    mascotDock,
    desktopCardDock: getSequenceValue(CARD_DOCK_SEQUENCE, stepIndex),
    mobileCardAnchor: getSequenceValue(MOBILE_ANCHOR_SEQUENCE, stepIndex),
    preferredMascotPose,
  };
}

/**
 * Build the full mascot choreography state used by the UI.
 */
export function buildMascotChoreography(
  step: TourStep,
  stepIndex: number,
  mascotConfig?: TourMascotConfig | null
): MascotChoreography {
  const stepChoreography = getStepChoreography(step, stepIndex, mascotConfig);
  const panelSide = resolveSideFromDock(stepChoreography.mascotDock);

  return {
    side: panelSide,
    position: resolvePositionFromDock(stepChoreography.mascotDock),
    dockPosition: stepChoreography.mascotDock,
    pointingDirection: stepChoreography.pointerDirection,
    panelSide,
    flipStyle: resolveFlipStyle(
      stepChoreography.mascotDock,
      stepChoreography.pointerDirection,
      stepChoreography.preferredMascotPose
    ),
    mascotImageStyle: resolveMascotImageStyle(stepChoreography.preferredMascotPose),
    preferredPose: stepChoreography.preferredMascotPose,
    mobileCardAnchor: stepChoreography.mobileCardAnchor,
  };
}

/**
 * Pick a fallback choreography when no step is selected (e.g. index screen).
 */
export function getIndexChoreography(
  mascotConfig?: TourMascotConfig | null
): MascotChoreography {
  const pose = mascotConfig?.default_poses?.index ?? 'intro_dude';
  const dock: MascotDockPosition =
    (mascotConfig?.choreography?.fallback_position as MascotDockPosition) ??
    'right-center';
  const pointer: MascotPointerDirection =
    (mascotConfig?.choreography?.fallback_pointer as MascotPointerDirection) ??
    'left';
  const side = resolveSideFromDock(dock);

  return {
    side,
    position: resolvePositionFromDock(dock),
    dockPosition: dock,
    pointingDirection: pointer,
    panelSide: side,
    flipStyle: resolveFlipStyle(dock, pointer, pose),
    mascotImageStyle: resolveMascotImageStyle(pose),
    preferredPose: pose,
    mobileCardAnchor: 'below',
  };
}

/**
 * Pick a celebration choreography for the end of a flow.
 */
export function getCelebrationChoreography(
  mascotConfig?: TourMascotConfig | null
): MascotChoreography {
  const pose = mascotConfig?.default_poses?.celebration ?? 'thumbs_up';
  const dock: MascotDockPosition = 'bottom-right';
  const pointer: MascotPointerDirection = 'up';

  return {
    side: 'right',
    position: 'bottom',
    dockPosition: dock,
    pointingDirection: pointer,
    panelSide: 'right',
    flipStyle: resolveFlipStyle(dock, pointer, pose),
    mascotImageStyle: resolveMascotImageStyle(pose),
    preferredPose: pose,
    mobileCardAnchor: 'above',
  };
}
